import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { DialogService } from 'src/app/services/UI/dialog.service';
import { ChurchBookSearchService } from 'src/app/services/church-book/church-book-search.service';
import { DialogBaseComponent } from '../../dialog/dialog-base/dialog-base.component';
import {
  ChurchBookSearchType,
  PersonResult,
  FamilyResult,
  EventResult,
  ChurchBookSearchResponse,
  EVENT_TYPE_LABELS,
} from 'src/app/common/models/church-book/church-book.models';

interface FieldOption {
  label: string;
  value: string;
}

interface ChurchBookBrowseData {
  searchType: ChurchBookSearchType;
}

type AnyResult = PersonResult | FamilyResult | EventResult;

const PERSON_FIELDS: FieldOption[] = [
  { label: 'churchbook.lbl_cb_first_name',    value: 'first_name' },
  { label: 'churchbook.lbl_cb_last_name',     value: 'last_name' },
  { label: 'churchbook.lbl_cb_gender',        value: 'gender' },
  { label: 'churchbook.lbl_cb_birth_year',    value: 'birth_year' },
  { label: 'churchbook.lbl_cb_birth_place',   value: 'birthplace' },
  { label: 'churchbook.lbl_cb_death_year',    value: 'death_year' },
  { label: 'churchbook.lbl_cb_death_place',   value: 'deathplace' },
  { label: 'churchbook.lbl_cb_baptism_year',  value: 'baptism_year' },
  { label: 'churchbook.lbl_cb_baptism_place', value: 'baptism_place' },
];

const FAMILY_FIELDS: FieldOption[] = [
  { label: 'churchbook.lbl_cb_member_name', value: 'member_name' },
  { label: 'churchbook.lbl_surname',        value: 'surname' },
  { label: 'churchbook.lbl_cb_parent_name', value: 'parent_name' },
  { label: 'churchbook.lbl_cb_location',    value: 'location' },
  { label: 'churchbook.lbl_cb_family_name', value: 'family_name' },
];

const EVENT_FIELDS: FieldOption[] = [
  { label: 'churchbook.lbl_cb_event_type', value: 'event_type' },
  { label: 'churchbook.lbl_cb_year',       value: 'year' },
  { label: 'churchbook.lbl_cb_month',      value: 'month' },
  { label: 'churchbook.lbl_cb_parish',     value: 'parish' },
  { label: 'churchbook.lbl_cb_location',   value: 'location' },
];

@Component({
  selector: 'app-church-book-browse',
  templateUrl: './church-book-browse.component.html',
  styleUrls: ['./church-book-browse.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChurchBookBrowseComponent extends DialogBaseComponent implements OnInit {
  public data: ChurchBookBrowseData;

  searchType!: ChurchBookSearchType;
  panelTitle!: string;
  fields!: FieldOption[];
  selectedField!: string;

  searchValue: string = '';
  selectedEventType: number | null = null;

  results: AnyResult[] = [];
  totalCount: number = 0;
  hasMore: boolean = false;
  offset: number = 0;

  isLoading: boolean = false;
  errorMessage: string = '';
  searched: boolean = false;
  showComingSoon: boolean = false;

  readonly eventTypeEntries = Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => ({
    value: Number(k),
    label: v,
  }));

  private currentSearch?: Subscription;

  constructor(
    private readonly ds: DialogService,
    private readonly ref1: ChangeDetectorRef,
    private readonly searchService: ChurchBookSearchService,
    private readonly destroyRef: DestroyRef,
  ) {
    super(ds, ref1);
  }

  ngOnInit(): void {
    this.searchType = this.data?.searchType ?? 'person';

    const titles: Record<ChurchBookSearchType, string> = {
      person: 'churchbook.lbl_browse_individuals',
      family: 'churchbook.lbl_browse_families',
      event:  'churchbook.lbl_browse_events',
    };
    const fieldMap: Record<ChurchBookSearchType, FieldOption[]> = {
      person: PERSON_FIELDS,
      family: FAMILY_FIELDS,
      event:  EVENT_FIELDS,
    };

    this.panelTitle    = titles[this.searchType];
    this.fields        = fieldMap[this.searchType];
    this.selectedField = this.fields[0].value;
  }

  get isEnumField(): boolean {
    return this.searchType === 'event' && this.selectedField === 'event_type';
  }

  get personResults(): PersonResult[] { return this.results as PersonResult[]; }
  get familyResults(): FamilyResult[] { return this.results as FamilyResult[]; }
  get eventResults():  EventResult[]  { return this.results as EventResult[];  }

  getEventTypeLabel(type: number): string {
    return EVENT_TYPE_LABELS[type] ?? String(type);
  }

  trackByResultId(_index: number, item: AnyResult): string {
    return item.id;
  }

  search(): void {
    if (this.isLoading) return;

    const value = this.isEnumField
      ? String(this.selectedEventType ?? '')
      : this.searchValue.trim();
    if (!value) return;

    this.isLoading    = true;
    this.errorMessage = '';
    this.offset       = 0;
    this.results      = [];
    this.searched     = true;

    this.currentSearch?.unsubscribe();
    this.currentSearch = this.searchService.search(this.searchType, this.selectedField, 'eq', value, 0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ChurchBookSearchResponse) => {
          this.results    = res.results;
          this.totalCount = res.metadata.total_count;
          this.hasMore    = res.metadata.has_more;
          this.offset     = 20;
          this.isLoading  = false;
          this.ref1.markForCheck();
        },
        error: () => {
          this.errorMessage = 'churchbook.err_search_failed';
          this.isLoading    = false;
          this.ref1.markForCheck();
        },
      });
  }

  loadMore(): void {
    if (!this.hasMore || this.isLoading) return;

    const value = this.isEnumField
      ? String(this.selectedEventType ?? '')
      : this.searchValue.trim();

    this.isLoading    = true;
    this.errorMessage = '';

    this.currentSearch = this.searchService.search(this.searchType, this.selectedField, 'eq', value, this.offset)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ChurchBookSearchResponse) => {
          this.results   = [...this.results, ...res.results];
          this.hasMore   = res.metadata.has_more;
          this.offset   += 20;
          this.isLoading = false;
          this.ref1.markForCheck();
        },
        error: () => {
          this.errorMessage = 'churchbook.err_load_more';
          this.isLoading    = false;
          this.ref1.markForCheck();
        },
      });
  }

  openComingSoon():  void { this.showComingSoon = true;  }
  closeComingSoon(): void { this.showComingSoon = false; }
}
